import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useIsLeader } from "@/hooks/useIsLeader";

const Auth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated, user } = useAuth();
  const { isLeader, isLoading: isLoadingLeader } = useIsLeader();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Parse query parameters for error message
  const searchParams = new URLSearchParams(location.search);
  const error = searchParams.get("error");

  const getErrorMessage = (error: string) => {
    switch (error) {
      case "company_not_found":
        return "Your email domain is not associated with any active company. Please contact support.";
      case "access_denied":
        return "Access denied. You do not have permission to access this application.";
      default:
        return "An authentication error occurred. Please try again.";
    }
  };

  const handleGoogleLogin = () => {
    setIsLoading(true);
    login();
  };

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    if (location.state?.from?.pathname) {
      navigate(location.state.from.pathname, { replace: true });
      return;
    }
    if (!isLoadingLeader) {
      navigate(isLeader ? "/my-team" : "/my-onething", { replace: true });
    }
  }, [isAuthenticated, user, isLoadingLeader, isLeader, navigate, location.state]);

  return (
    <div className="min-h-screen bg-background">
      {/* Background */}
      <div className="fixed inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
      <div className="fixed top-1/4 left-1/4 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
      <div className="fixed bottom-1/4 right-1/4 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />

      <div className="relative flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Back Link */}
          <Link
            to="/"
            className="mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>

          {/* Auth Card */}
          <div className="rounded-2xl border border-border bg-card/80 p-8 shadow-soft backdrop-blur-xl animate-scale-in">
            {/* Logo */}
            <div className="mb-8 text-center">
              <div className="mx-auto mb-6 flex items-center justify-center">
                <img
                  src="/infina-logo.png"
                  alt="Infina1t Logo"
                  className="h-8 w-auto object-contain drop-shadow-lg"
                />
              </div>
              <h1 className="text-2xl font-bold">Welcome to Infina1t</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Sign in to manage your priorities with AI
              </p>
            </div>

            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Authentication Error</AlertTitle>
                <AlertDescription>
                  {getErrorMessage(error)}
                </AlertDescription>
              </Alert>
            )}

            <Button
              variant="outline"
              className="w-full gap-2 relative h-12 text-base"
              onClick={handleGoogleLogin}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Connecting...</span>
                </div>
              ) : (
                <>
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Continue with Google
                </>
              )}
            </Button>

            <p className="mt-6 text-center text-xs text-muted-foreground">
              By continuing, you agree to our Terms of Service and Privacy Policy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
